import { validOps, type OpsPayload } from '../../shared/contract'

/**
 * The report on screen. The last good copy paints first, from localStorage,
 * before the network is asked anything; a fresh one replaces it whole.
 * Offline and failed reads are quiet facts for the status line, not errors.
 */

const KEY = 'ops:now'
const REFRESH_MS = 2 * 60_000
const TIMEOUT_MS = 10_000
/** With nothing on screen yet, a failed read is tried again sooner. */
const RETRY_MS = 10_000

interface Stored {
  data: OpsPayload
  etag?: string
}

function readStored(): Stored | null {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Stored | null
    return stored && validOps(stored.data) ? stored : null
  } catch {
    return null
  }
}

export class Report {
  data = $state.raw<OpsPayload | null>(null)
  /** True once this session has had an answer from the edge. */
  confirmed = $state(false)
  /** The edge answered with its last good copy: the resort's report was unreachable. */
  stale = $state(false)
  failed = $state(false)
  loading = $state(false)
  online = $state(typeof navigator === 'undefined' ? true : navigator.onLine)
  #etag: string | undefined
  #timer: ReturnType<typeof setTimeout> | undefined
  /** The read in flight, aborted when reading stops. */
  #abort: AbortController | undefined
  #running = false

  constructor() {
    const stored = readStored()
    if (stored) {
      this.data = stored.data
      this.#etag = stored.etag
    }
  }

  /** Starts reading and keeps reading while the page is visible. Returns a stop function. */
  start(): () => void {
    const visible = () => {
      if (document.visibilityState === 'visible') void this.refresh()
      else clearTimeout(this.#timer)
    }
    const online = () => {
      this.online = true
      void this.refresh()
    }
    const offline = () => (this.online = false)
    document.addEventListener('visibilitychange', visible)
    addEventListener('online', online)
    addEventListener('offline', offline)
    this.#running = true
    void this.refresh()
    return () => {
      this.#running = false
      clearTimeout(this.#timer)
      this.#abort?.abort()
      document.removeEventListener('visibilitychange', visible)
      removeEventListener('online', online)
      removeEventListener('offline', offline)
    }
  }

  async refresh(): Promise<void> {
    clearTimeout(this.#timer)
    if (this.loading) return
    this.loading = true
    const abort = (this.#abort = new AbortController())
    const timeout = setTimeout(() => abort.abort(), TIMEOUT_MS)
    try {
      const headers: HeadersInit = { accept: 'application/json' }
      if (this.#etag && this.data) headers['if-none-match'] = this.#etag
      const res = await fetch('/api/now.json', {
        headers,
        cache: 'no-cache',
        signal: abort.signal,
      })
      if (res.status === 304) {
        // The edge still marks its last good copy when the resort's report is down.
        this.stale = res.headers.get('x-ops-stale') === '1'
        this.confirmed = true
        this.failed = false
      } else if (res.ok) {
        const data: unknown = await res.json()
        if (!validOps(data)) throw new Error('invalid report')
        this.data = data
        this.#etag = res.headers.get('etag') ?? undefined
        this.stale = res.headers.get('x-ops-stale') === '1'
        this.confirmed = true
        this.failed = false
        try {
          localStorage.setItem(KEY, JSON.stringify({ data, etag: this.#etag } satisfies Stored))
        } catch {
          // A full or blocked store only costs the next launch its instant paint.
        }
      } else {
        this.failed = true
      }
    } catch {
      this.failed = true
    } finally {
      clearTimeout(timeout)
      this.loading = false
      if (this.#running && document.visibilityState === 'visible')
        this.#timer = setTimeout(() => void this.refresh(), this.data ? REFRESH_MS : RETRY_MS)
    }
  }
}

export const report = new Report()
